import {useMemo, useState, useSyncExternalStore} from 'react';
import {
  CartAccessory,
  CartSheet,
  CategoryRail,
  MobileOrderingShell,
  MutationFeedback,
  OrderingHeader,
  ProductList,
  ServiceContext,
  UiButton,
  UiCheckbox,
  UiRadioGroup,
} from '@morefunos/ui';
import type {ProductOptionSelectionInput} from '../../../packages/order/product-selection.ts';
import type {SmtP01WorkspaceMutationResult} from '../../../packages/runtime/smt-p01-workspace-contract.ts';
import type {SmmOrderingConfiguration, SmmOrderingPort} from './smm-ordering-port.ts';

export interface OrderingPageProps {
  readonly port: SmmOrderingPort | null;
}

type Feedback = {
  readonly tone: 'info' | 'positive' | 'warning' | 'critical';
  readonly message: string;
  readonly detail?: string;
};

const mutationFeedback = (result: SmtP01WorkspaceMutationResult<void>, acceptedMessage: string): Feedback =>
  result.kind === 'accepted'
    ? {tone: 'positive', message: acceptedMessage}
    : {tone: 'warning', message: '操作未接受', detail: result.code};

function ProductConfigurationForm({
  config,
  port,
  onFeedback,
}: {
  readonly config: SmmOrderingConfiguration;
  readonly port: SmmOrderingPort;
  readonly onFeedback: (feedback: Feedback) => void;
}) {
  const [selected, setSelected] = useState<Record<string, readonly string[]>>({});

  const changeSingle = (groupId: string, optionId: string): void => {
    setSelected((current) => ({...current, [groupId]: [optionId]}));
  };

  const toggleMultiple = (groupId: string, optionId: string, max: number): void => {
    setSelected((current) => {
      const existing = current[groupId] ?? [];
      const next = existing.includes(optionId)
        ? existing.filter((candidate) => candidate !== optionId)
        : existing.length < max ? [...existing, optionId] : existing;
      return {...current, [groupId]: next};
    });
  };

  const valid = config.groups.every((group) => {
    const count = selected[group.optionGroupId]?.length ?? 0;
    return count >= group.min && count <= group.max;
  });

  const submit = (): void => {
    if (!valid) return;
    const selections: ProductOptionSelectionInput[] = config.groups.flatMap((group) => {
      const optionIds = selected[group.optionGroupId] ?? [];
      return optionIds.length ? [{optionGroupId: group.optionGroupId, optionIds}] : [];
    });
    const result = port.configuredAdd(config.productId, selections);
    onFeedback(mutationFeedback(result, `${config.productName} 已更新購物車`));
  };

  return (
    <div className="smm-ordering-configurator">
      {config.groups.map((group) => {
        const values = selected[group.optionGroupId] ?? [];
        if (group.max === 1) {
          return (
            <UiRadioGroup
              key={group.optionGroupId}
              label={`${group.label}${group.required ? ' *' : ''}`}
              value={values[0] ?? null}
              onChange={(value) => changeSingle(group.optionGroupId, value)}
              items={group.options.map((option) => ({
                value: option.optionId,
                label: option.priceDeltaLabel ? `${option.label} ${option.priceDeltaLabel}` : option.label,
                isDisabled: !option.enabled,
                'aria-label': option.label,
              }))}
            />
          );
        }
        return (
          <fieldset key={group.optionGroupId} className="smm-ordering-option-group">
            <legend>{group.label}{group.required ? ' *' : ''}</legend>
            {group.options.map((option) => (
              <UiCheckbox
                key={option.optionId}
                label={option.priceDeltaLabel ? `${option.label} ${option.priceDeltaLabel}` : option.label}
                isSelected={values.includes(option.optionId)}
                isDisabled={!option.enabled}
                onChange={() => toggleMultiple(group.optionGroupId, option.optionId, group.max)}
              />
            ))}
          </fieldset>
        );
      })}
      <UiButton variant="primary" isDisabled={!valid} onPress={submit}>加入購物車</UiButton>
    </div>
  );
}

function RuntimeBlockedOrdering() {
  return (
    <section className="smm-page" aria-labelledby="smm-ordering-title">
      <MobileOrderingShell
        header={(
          <OrderingHeader
            eyebrow="Mobile Ordering"
            title="點單"
            subtitle="Customer / SMM 共用點餐軀體"
          />
        )}
        feedback={(
          <MutationFeedback
            tone="critical"
            message="Runtime 未就緒"
            detail="未收到正式 Business Runtime、Catalog 同 businessDate，SMM 唔會建立假商品、假價錢或假購物車。"
          />
        )}
        categories={<CategoryRail items={[]} emptyLabel="等待商品分類" />}
        products={<ProductList items={[]} emptyLabel="等待正式商品投影；目前不顯示示範商品。" />}
        accessory={(
          <CartAccessory
            countLabel="0"
            totalLabel="—"
            actionLabel="查看購物車"
            isDisabled
            disabledReason="正式購物車尚未接入"
          >
            <CartSheet items={[]} totalLabel="—" />
          </CartAccessory>
        )}
      />
    </section>
  );
}

export function OrderingPage({port}: OrderingPageProps) {
  if (!port) return <RuntimeBlockedOrdering />;
  return <ConnectedOrderingPage port={port} />;
}

function ConnectedOrderingPage({port}: {readonly port: SmmOrderingPort}) {
  const snapshot = useSyncExternalStore(port.subscribe, port.getSnapshot, port.getSnapshot);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
  const [feedback, setFeedback] = useState<Feedback>({tone: 'info', message: '已連接 Business Runtime'});
  const activeCategoryId = snapshot.categories.some((item) => item.id === selectedCategoryId)
    ? selectedCategoryId
    : snapshot.categories[0]?.id;
  const visibleProducts = useMemo(
    () => activeCategoryId
      ? snapshot.products.filter((item) => item.categoryId === activeCategoryId)
      : snapshot.products,
    [activeCategoryId, snapshot.products],
  );

  const directAdd = (productId: string): void => {
    try {
      setFeedback(mutationFeedback(port.directAdd(productId), '已更新購物車'));
    } catch (error) {
      setFeedback({tone: 'critical', message: '加入失敗', detail: error instanceof Error ? error.message : 'SMM_ORDERING_REJECTED'});
    }
  };

  const changeQuantity = (lineId: string, delta: number): void => {
    try {
      setFeedback(mutationFeedback(port.changeQuantity(lineId, delta), '購物車數量已更新'));
    } catch (error) {
      setFeedback({tone: 'critical', message: '數量更新失敗', detail: error instanceof Error ? error.message : 'SMM_ORDERING_REJECTED'});
    }
  };

  const removeLine = (lineId: string): void => {
    try {
      setFeedback(mutationFeedback(port.removeLine(lineId), '已從購物車移除'));
    } catch (error) {
      setFeedback({tone: 'critical', message: '移除失敗', detail: error instanceof Error ? error.message : 'SMM_ORDERING_REJECTED'});
    }
  };

  const confirmCombo = (proposalId: string): void => {
    try {
      setFeedback(mutationFeedback(port.confirmCombo(proposalId), '套餐已按 Business Runtime 組合'));
    } catch (error) {
      setFeedback({tone: 'critical', message: '套餐組合失敗', detail: error instanceof Error ? error.message : 'SMM_COMBO_REJECTED'});
    }
  };

  const setServiceMode = (mode: 'takeaway' | 'dine-in'): void => {
    try {
      setFeedback(mutationFeedback(port.setServiceMode(mode), mode === 'dine-in' ? '已切換堂食' : '已切換外賣'));
    } catch (error) {
      setFeedback({tone: 'critical', message: '服務模式更新失敗', detail: error instanceof Error ? error.message : 'SMM_ORDERING_REJECTED'});
    }
  };

  const finalize = async (): Promise<void> => {
    try {
      const result = await port.finalize();
      setFeedback({tone: 'positive', message: '訂單已建立', detail: result.order.orderId});
    } catch (error) {
      setFeedback({tone: 'critical', message: '落單失敗', detail: error instanceof Error ? error.message : 'SMM_ORDERING_REJECTED'});
    }
  };

  return (
    <section className="smm-page" aria-labelledby="smm-ordering-title">
      <MobileOrderingShell
        header={(
          <OrderingHeader
            eyebrow="Mobile Ordering"
            title="點單"
            subtitle={`Catalog ${snapshot.catalogRevision}`}
            serviceContext={(
              <ServiceContext items={[{
                id: 'service-mode',
                label: snapshot.serviceMode === 'dine-in' ? '堂食' : snapshot.serviceMode === 'takeaway' ? '外賣' : '未選服務模式',
                tone: snapshot.serviceMode ? 'positive' : 'warning',
              }]} />
            )}
          />
        )}
        feedback={<MutationFeedback tone={feedback.tone} message={feedback.message} detail={feedback.detail} />}
        categories={(
          <CategoryRail
            items={snapshot.categories}
            selectedId={activeCategoryId}
            onSelect={setSelectedCategoryId}
            emptyLabel="目前沒有可顯示分類"
          />
        )}
        products={(
          <ProductList
            items={visibleProducts}
            onDirectAction={directAdd}
            emptyLabel="目前沒有可顯示商品"
            configureContent={(product) => {
              const config = port.configurationFor(product.id);
              return config
                ? <ProductConfigurationForm config={config} port={port} onFeedback={setFeedback} />
                : <MutationFeedback tone="critical" message="配置不可用" detail="Business Runtime 未提供商品配置投影" />;
            }}
          />
        )}
        accessory={(
          <CartAccessory
            countLabel={snapshot.cart.countLabel}
            totalLabel={snapshot.cart.totalLabel}
            actionLabel="查看購物車"
            isDisabled={snapshot.cart.lines.length === 0}
            disabledReason={snapshot.cart.lines.length === 0 ? '購物車暫時未有項目' : undefined}
          >
            <CartSheet
              items={snapshot.cart.lines}
              totalLabel={snapshot.cart.totalLabel}
              statusLabel={snapshot.cart.statusLabel}
              statusTone={snapshot.cart.pricingStatus === 'quoted' ? 'positive' : snapshot.cart.checkoutBlocked ? 'warning' : 'info'}
              primaryActionLabel="確認落單"
              primaryActionDisabled={snapshot.cart.checkoutBlocked}
              primaryActionReason={snapshot.cart.checkoutBlockingCodes.join(' · ') || undefined}
              onDecrease={(lineId) => changeQuantity(lineId, -1)}
              onIncrease={(lineId) => changeQuantity(lineId, 1)}
              onRemove={removeLine}
              onPrimaryAction={() => { void finalize(); }}
              footer={(
                <div className="smm-ordering-cart-footer">
                  {snapshot.combo.proposals.length > 0 ? (
                    <section className="smm-ordering-combo" aria-label="套餐建議">
                      <strong>套餐建議</strong>
                      {snapshot.combo.proposals.map((proposal) => (
                        <div key={proposal.proposalId} className="smm-ordering-combo-proposal">
                          <div>
                            <div className="smm-ordering-combo-rule">{proposal.ruleLabel}</div>
                            <div className="smm-ordering-combo-meta">
                              <span>可節省 {proposal.savingLabel}</span>
                              <span>組合後 {proposal.resultingTotalLabel}</span>
                            </div>
                          </div>
                          <UiButton variant="secondary" onPress={() => confirmCombo(proposal.proposalId)}>
                            一鍵組合 {proposal.ruleLabel}
                          </UiButton>
                        </div>
                      ))}
                    </section>
                  ) : null}
                  {snapshot.combo.groups.length > 0 ? (
                    <div className="smm-ordering-combo-active" aria-label="已組合套餐">
                      {snapshot.combo.groups.map((group) => (
                        <span key={group.groupId}>{group.ruleId} · {group.status === 'complete' ? '已完成' : '待補齊'}</span>
                      ))}
                    </div>
                  ) : null}
                  <div className="smm-ordering-service-mode" aria-label="服務模式">
                    <UiButton
                      variant={snapshot.serviceMode === 'takeaway' ? 'primary' : 'secondary'}
                      onPress={() => setServiceMode('takeaway')}
                    >外賣</UiButton>
                    <UiButton
                      variant={snapshot.serviceMode === 'dine-in' ? 'primary' : 'secondary'}
                      onPress={() => setServiceMode('dine-in')}
                    >堂食</UiButton>
                  </div>
                </div>
              )}
            />
          </CartAccessory>
        )}
      />
    </section>
  );
}
